import React, { useEffect } from 'react'
import './EmailStyle.css'
import { Button, Tabs } from '@enamelai/design-system'
import useCommunicationsLeaveGuard from '../shared/useCommunicationsLeaveGuard'
import useEmailSettings, { type EmailTab } from './useEmail'
import GeneralTab from './General/General.component'
import InboxTab from './Inbox/Inbox.component'
import TagsTab from './Tags/Tags.component'
import AdvancedTab from './Advanced/Advanced.component'
import SettingsSkeleton from '@pages/Settings/SettingsSkeleton.page'
import { useSettingsTabSkeleton } from '@pages/Settings/useSettingsTabSkeleton'

const EMAIL_TABS: { label: string; id: EmailTab }[] = [
  { label: 'General', id: 'general' },
  { label: 'Inbox', id: 'inbox' },
  { label: 'Labels', id: 'tags' },
  { label: 'Advanced', id: 'advanced' },
]

const EmailSettings = (): React.ReactElement => {
  const hooks = useEmailSettings()

  const {
    setHeaderButtons,
    hasChanges,
    loading,
    handleReset,
    handleSave,
    tab,
    setTab,
    data,
    handleChange,
    handleInboxChange,
    handleAdvancedChange,
    handleRemoveTag,
    handleCreateTag,
    handleUpdateTag,
    handleGoogleMailboxConnected,
    handleGoogleMailboxDisconnected,
    dataReady,
  } = hooks
  const isTabSkeleton = useSettingsTabSkeleton(tab)
  const leaveGuard = useCommunicationsLeaveGuard(hasChanges && dataReady)

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button
          size="sm"
          type="button"
          variant="secondary"
          disabled={!hasChanges || loading || !dataReady}
          onClick={() => leaveGuard.requestLeave(handleReset)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          type="button"
          variant="primary"
          disabled={!hasChanges || loading || !dataReady}
          onClick={() => {
            void handleSave()
          }}
        >
          Save changes
        </Button>
      </>,
    )
    return () => {
      setHeaderButtons(null)
    }
  }, [
    dataReady,
    hasChanges,
    loading,
    handleReset,
    handleSave,
    leaveGuard.requestLeave,
    setHeaderButtons,
  ])

  const renderTab = (): React.ReactElement => {
    switch (tab) {
      case 'general':
        return (
          <GeneralTab
            data={data}
            onChange={handleChange}
            onGoogleMailboxConnected={handleGoogleMailboxConnected}
            onGoogleMailboxDisconnected={handleGoogleMailboxDisconnected}
          />
        )
      case 'inbox':
        return <InboxTab data={data.inbox} onChange={handleInboxChange} />
      case 'tags':
        return (
          <TagsTab
            tags={data.tags}
            onRemoveTag={handleRemoveTag}
            onCreateTag={handleCreateTag}
            onUpdateTag={handleUpdateTag}
          />
        )
      case 'advanced':
        return (
          <AdvancedTab data={data.advanced} onChange={handleAdvancedChange} />
        )
      default:
        return (
          <GeneralTab
            data={data}
            onChange={handleChange}
            onGoogleMailboxConnected={handleGoogleMailboxConnected}
            onGoogleMailboxDisconnected={handleGoogleMailboxDisconnected}
          />
        )
    }
  }

  return (
    <div className="scp-email-container" id="settings-email">
      {leaveGuard.modal}
      {!dataReady ? (
        <SettingsSkeleton variant="form" />
      ) : (
        <>
          <Tabs
            orientation="horizontal"
            items={EMAIL_TABS}
            value={tab}
            onChange={(value) => {
              const next = value as EmailTab
              if (next === tab) return
              leaveGuard.requestLeave(() => {
                handleReset()
                setTab(next)
              })
            }}
          />
          {isTabSkeleton ? <SettingsSkeleton variant="form" /> : renderTab()}
        </>
      )}
    </div>
  )
}

export default EmailSettings
